import getSliceName from '@om-fe/core/utils/get-slice-name';
import { getMatchListNrOfMarkets } from '../../utils/match-list.utils';
import * as shallowEqual from 'shallowequal';
import {
    FAVORITE_MATCH_AGGREGATOR_PATH,
    FAVORITE_MATCH_AGGREGATOR_PATH_LOGGED_IN,
    FavoriteMatchListDisciplinesTopic,
    FavoriteMatchListPublicProps,
    FavoriteMatchListTopic,
    FavoritesAggregatorOneOfTypeModel,
    favoriteSlicePrefix,
} from './favorite-match-list.constants';
import {
    AggregatorBettingOfferModel,
    AggregatorDisciplineModel,
    AggregatorEventInfoModel,
    AggregatorMainMarketModel,
    AggregatorMarketModel,
    AggregatorMatchModel,
    AggregatorOneOfTypeModel,
    AggregatorOutcomeModel,
    AggregatorStreamingProviderEvent,
    AggregatorTournamentsModel,
    DisciplineLiveStatus,
    ModelTypes,
} from '@om-fe/models';
import {
    RecordCreate,
    RecordDelete,
    RecordEvent,
    RecordUpdate,
    UpdateActionJournal,
    UpdateActionPayload,
} from '@om-fe/api';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

export function getFavoriteMatchListSliceName(instanceId: string): string {
    return getSliceName(favoriteSlicePrefix, instanceId);
}

export function getFavoriteMatchListTopic(
    matchId: string,
    mainMarketsLimit?: number,
): FavoriteMatchListTopic | FavoriteMatchListDisciplinesTopic {
    const globalConfig: OMFE2ConfigModel = getConfig();
    const isAuthenForTopic: boolean = !!globalConfig?.enabledTopicDelay;
    const topic: string = isAuthenForTopic ? FAVORITE_MATCH_AGGREGATOR_PATH_LOGGED_IN : FAVORITE_MATCH_AGGREGATOR_PATH;
    const nrOfMarkets: number = getMatchListNrOfMarkets(mainMarketsLimit);

    return {
        path: topic,
        params: {
            matchId,
            nrOfMarkets,
        },
    };
}

export interface FavoriteMatchListTopicProps {
    readonly userID?: string | number;
    readonly liveStatus: DisciplineLiveStatus;
    readonly mainMarketsLimit: number | undefined;
    readonly isDefault: boolean | undefined;
}

function getTopicProps(props: FavoriteMatchListPublicProps): FavoriteMatchListTopicProps {
    return {
        userID: props.userID,
        liveStatus: props.liveStatus,
        mainMarketsLimit: props.mainMarketsLimit,
        isDefault: props.isDefault,
    };
}

export function areFavoriteMatchListTopicPropsEqual(
    prevProps: FavoriteMatchListPublicProps,
    newProps: FavoriteMatchListPublicProps,
): boolean {
    return shallowEqual(getTopicProps(prevProps), getTopicProps(newProps));
}

export function getFavoriteSportIds(records: AggregatorOneOfTypeModel[]): string[] {
    const mutableDisciplineList: string[] = [];

    records.forEach((item: AggregatorOneOfTypeModel) => {
        if (item._type === ModelTypes.SPORT) {
            mutableDisciplineList.push(item.id);
        }
    });

    return mutableDisciplineList;
}

export function getFavoriteSportIdInMatchs(records: AggregatorOneOfTypeModel[]): string[] {
    const mutableDisciplineList: string[] = [];

    records.forEach((item: AggregatorOneOfTypeModel) => {
        if (item._type === ModelTypes.MATCH) {
            mutableDisciplineList.push(item.sportId);
        }
    });

    return mutableDisciplineList;
}

export function getFavoriteEventsFromRecords(records: FavoritesAggregatorOneOfTypeModel[]): string[] {
    const mutableEventsList: string[] = [];

    records.forEach((item: FavoritesAggregatorOneOfTypeModel) => {
        if (item._type === ModelTypes.MATCH) {
            mutableEventsList.push(item.id);
        }
    });

    return mutableEventsList;
}

export function getDeletedMatchesElementsFromRecords(payload: UpdateActionPayload<FavoritesAggregatorOneOfTypeModel>): string[] {
    const { journal: { deletes }, summary: { deleted } } = payload;
    const deletedMatchesElements: string[] = [];

    if (deleted > 0) {
        deletes.forEach((element: RecordDelete) => {
            if (element.entityType === ModelTypes.MATCH) {
                deletedMatchesElements.push(element.id);
            }
        });
    }

    return deletedMatchesElements;
}

export function getCreatedMatchesElementsFromRecords(payload: UpdateActionPayload<FavoritesAggregatorOneOfTypeModel>): string[] {
    const { journal: { creates }, summary: { created } } = payload;
    const createdMatchesElements: string[] = [];

    if (created > 0) {
        creates.forEach((element: RecordCreate<FavoritesAggregatorOneOfTypeModel>) => {
            if (element.entityType === ModelTypes.MATCH) {
                createdMatchesElements.push(element.id);
            }
        });
    }

    return createdMatchesElements;
}

export function getMappedSet(
    payload: UpdateActionPayload<AggregatorOneOfTypeModel>,
): Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>> {
    return payload.records
        .reduce<Partial<Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>>>>((
            mutableAcc: Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>>,
            item: AggregatorOneOfTypeModel,
        ): Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>> => {
            if (!mutableAcc[item._type]) {
                mutableAcc[item._type] = {};
            }
            mutableAcc[item._type][item.id] = item;

            return mutableAcc;
        }, {}) as Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>>;
}

export function findDisciplineId(
    updateElement: RecordEvent<AggregatorOneOfTypeModel>,
    mappedSet: Record<ModelTypes, Record<string, AggregatorOneOfTypeModel>>,
): string | null {
    const { entityType, id } = updateElement;
    switch (entityType) {
        case ModelTypes.SPORT:
            return id;
        case ModelTypes.MATCH:
            return (mappedSet.MATCH[id] as AggregatorMatchModel).sportId;
        case ModelTypes.MAIN_MARKET:
            return (mappedSet.MAIN_MARKET[id] as AggregatorMainMarketModel).sportId;
        case ModelTypes.EVENT_INFO:
            return (mappedSet.MATCH[(mappedSet.EVENT_INFO[id] as AggregatorEventInfoModel).eventId] as AggregatorMatchModel).sportId;
        case ModelTypes.MARKET:
            return (mappedSet.MATCH[(mappedSet.MARKET[id] as AggregatorMarketModel).eventId] as AggregatorMatchModel).sportId;
        case ModelTypes.OUTCOME:
            return (mappedSet.MATCH[(mappedSet.OUTCOME[id] as AggregatorOutcomeModel).eventId] as AggregatorMatchModel).sportId;
        case ModelTypes.STREAMING_PROVIDER_EVENT_RELATION:
            return (mappedSet.MATCH[(mappedSet.STREAMING_PROVIDER_EVENT_RELATION[id] as AggregatorStreamingProviderEvent).eventId] as AggregatorMatchModel).sportId;
        case ModelTypes.BETTING_OFFER:
            return (mappedSet.MATCH[(mappedSet.OUTCOME[(mappedSet.BETTING_OFFER[id] as AggregatorBettingOfferModel).outcomeId] as AggregatorOutcomeModel).eventId] as AggregatorMatchModel).sportId;
        case ModelTypes.MARKET_OUTCOME_RELATION:
            return (mappedSet.MATCH[(mappedSet.OUTCOME[(mappedSet.MARKET_OUTCOME_RELATION[id] as AggregatorBettingOfferModel).outcomeId] as AggregatorOutcomeModel).eventId] as AggregatorMatchModel).sportId;
        default:
            return null;
    }
}

export function createEmptyJournal(): UpdateActionJournal<any> {
    return { creates: [], updates: [], deletes: [] };
}

export function filterFavoriteMatchListPayload(payload: UpdateActionPayload<FavoritesAggregatorOneOfTypeModel>): UpdateActionPayload<AggregatorOneOfTypeModel> {
    const newRecords: AggregatorOneOfTypeModel[] = filterFavoriteMatchListRecords(payload.records);
    const newJournal: UpdateActionJournal<AggregatorOneOfTypeModel> = filterFavoriteMatchListJournal(payload.journal);

    return { ...payload, records: newRecords, journal: newJournal };
}

function filterFavoriteMatchListRecords(records: FavoritesAggregatorOneOfTypeModel[]): AggregatorOneOfTypeModel[] {
    const matchRecordsSportIds: string[] = records.filter(
        (record: FavoritesAggregatorOneOfTypeModel) => record._type === ModelTypes.MATCH,
    ).map((match: AggregatorMatchModel) => match.sportId);

    return records.filter((record: FavoritesAggregatorOneOfTypeModel) => {
        if (record._type === ModelTypes.SPORT) {
            return matchRecordsSportIds.includes(record.id);
        } else if (record._type === ModelTypes.MAIN_MARKET) {
            return matchRecordsSportIds.includes(record.sportId);
        }

        return record._type !== ModelTypes.TOURNAMENT;
    }) as AggregatorOneOfTypeModel[];
}

function filterFavoriteMatchListJournal(
    journal: UpdateActionJournal<FavoritesAggregatorOneOfTypeModel>,
): UpdateActionJournal<AggregatorOneOfTypeModel> {
    const { creates, deletes, updates } = journal;
    let finalJournal: UpdateActionJournal<AggregatorOneOfTypeModel> = createEmptyJournal();

    if (!creates && !updates && !deletes) {
        return journal as UpdateActionJournal<AggregatorOneOfTypeModel>;
    }

    if (creates) {
        const tournamentCreatesJournal: Array<RecordCreate<AggregatorTournamentsModel>> = creates.filter(
            (journalItem: RecordCreate<FavoritesAggregatorOneOfTypeModel>) => journalItem.entityType === ModelTypes.TOURNAMENT,
        ) as Array<RecordCreate<AggregatorTournamentsModel>>;

        if (tournamentCreatesJournal.length > 0) {
            const favoriteTournamentCreatesDisciplineId: string[] = tournamentCreatesJournal.map(
                (journalItem: RecordCreate<AggregatorTournamentsModel>) => journalItem.entity.sportId);

            creates.forEach((journalItem: RecordCreate<FavoritesAggregatorOneOfTypeModel>) => {
                if (journalItem.entityType === ModelTypes.SPORT || journalItem.entityType === ModelTypes.MAIN_MARKET) {
                    if (journalItem.entityType === ModelTypes.SPORT && !favoriteTournamentCreatesDisciplineId.includes(journalItem.id)) {
                        finalJournal.creates.push(journalItem as RecordCreate<AggregatorDisciplineModel>);
                    } else if (
                        journalItem.entityType === ModelTypes.MAIN_MARKET
                        && !favoriteTournamentCreatesDisciplineId.includes((journalItem.entity as AggregatorMainMarketModel).sportId)
                    ) {
                        finalJournal.creates.push(journalItem as RecordCreate<AggregatorMainMarketModel>);
                    }
                } else if (journalItem.entityType !== ModelTypes.TOURNAMENT) {
                    finalJournal.creates.push(journalItem as RecordCreate<AggregatorOneOfTypeModel>);
                }
            });
        } else {
            finalJournal = {
                ...finalJournal,
                creates: creates as Array<RecordCreate<AggregatorOneOfTypeModel>>,
            };
        }
    }
    if (updates) {
        const tournamentUpdateJournal: Array<RecordUpdate<AggregatorTournamentsModel>> = updates.filter(
            (journalItem: RecordUpdate<FavoritesAggregatorOneOfTypeModel>) => journalItem.entityType === ModelTypes.TOURNAMENT,
        ) as Array<RecordUpdate<AggregatorTournamentsModel>>;

        if (
            tournamentUpdateJournal.length > 0
            && !tournamentUpdateJournal.some((entity: RecordUpdate<AggregatorTournamentsModel>) => {
                return entity.changedProperties.sportId === undefined;
            })
        ) {
            const favoriteTournamentUpdatesDisciplineId: string[] = tournamentUpdateJournal.map(
                (journalItem: RecordUpdate<AggregatorTournamentsModel>) => journalItem.changedProperties.sportId) as string[];

            updates.forEach((journalItem: RecordUpdate<FavoritesAggregatorOneOfTypeModel>) => {
                if (journalItem.entityType === ModelTypes.SPORT || journalItem.entityType === ModelTypes.MAIN_MARKET) {
                    if (journalItem.entityType === ModelTypes.SPORT
                        && !favoriteTournamentUpdatesDisciplineId.includes(journalItem.id)
                    ) {
                        finalJournal.updates.push(journalItem as RecordUpdate<AggregatorDisciplineModel>);
                    } else if (
                        journalItem.entityType === ModelTypes.MAIN_MARKET
                        && !favoriteTournamentUpdatesDisciplineId.includes((journalItem.changedProperties as AggregatorMainMarketModel).sportId)
                    ) {
                        finalJournal.updates.push(journalItem as RecordUpdate<AggregatorMainMarketModel>);
                    }
                } else if (journalItem.entityType !== ModelTypes.TOURNAMENT) {
                    finalJournal.updates.push(journalItem as RecordUpdate<AggregatorOneOfTypeModel>);
                }
            });
        } else {
            finalJournal = {
                ...finalJournal,
                updates: updates as Array<RecordUpdate<AggregatorOneOfTypeModel>>,
            };
        }
    }
    if (deletes) {
        finalJournal = {
            ...finalJournal,
            deletes,
        };
    }

    return finalJournal;
}

export function findDeletedElement(
    deletedElement: RecordDelete,
    mappedSet: Record<ModelTypes, Record<string, FavoritesAggregatorOneOfTypeModel>>,
): boolean {
    const { entityType, id } = deletedElement;

    if (entityType === ModelTypes.TOURNAMENT) {
        return false;
    }

    return !!mappedSet[entityType][id];
}
